import {
  usePeopleAnalyticsDashboards,
  usePeopleAnalyticsWidgetData,
} from "@/queries/reporting/use-people-analytics.query.ts";
import {
  ApiPeopleAnalyticsEmployeeFilter,
  ApiPeopleAnalyticsWidgetDataInput,
} from "@/types/reporting/people-analytics.types.ts";
import {
  ApiWidgetDimension,
  ApiWidgetMetric,
  ApiWidgetPreviewInput,
  ApiWidgetType,
} from "@/types/reporting/reporting.types.ts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const COLUMN_ID_BY_DIMENSION: Partial<Record<ApiWidgetDimension, string>> = {
  [ApiWidgetDimension.department]: "employee.position.department",
  [ApiWidgetDimension.division]: "employee.position.division",
  [ApiWidgetDimension.location]: "employee.position.location",
  [ApiWidgetDimension.job_profile]: "employee.position.job_profile",
  [ApiWidgetDimension.job_role]: "employee.position.job_role",
  [ApiWidgetDimension.termination_reason]: "employee.termination.reason",
  [ApiWidgetDimension.termination_leave_type]: "employee.termination.leave_type",
};

// Хвосты system_key из base/people_analytics/widgets/people_analytics_widget_constants.py
const WIDGET_KEY_BY_METRIC: Partial<Record<ApiWidgetMetric, string>> = {
  [ApiWidgetMetric.headcount]: "headcount",
  [ApiWidgetMetric.headcount_growth_rate]: "growth",
  [ApiWidgetMetric.hires]: "joiners",
  [ApiWidgetMetric.terminations]: "leavers",
  [ApiWidgetMetric.managers_count]: "managers",
  [ApiWidgetMetric.manager_to_employee_ratio]: "manager_ratio",
};

const TERMINATION_DIMENSIONS = [
  ApiWidgetDimension.termination_reason,
  ApiWidgetDimension.termination_leave_type,
];

function resolveSystemWidget(input: ApiWidgetPreviewInput): {
  systemKey: string;
  splitByColumnId: string | null;
} {
  const metrics = input.query.metrics;
  const dimension = input.query.dimensions?.[0];
  const splitByColumnId = dimension ? (COLUMN_ID_BY_DIMENSION[dimension] ?? null) : null;
  const metricKey = WIDGET_KEY_BY_METRIC[metrics[0]] ?? metrics[0];

  if (input.widget_type === ApiWidgetType.kpi) {
    return { systemKey: `headcount.kpi.${metricKey}`, splitByColumnId: null };
  }
  if (metrics.includes(ApiWidgetMetric.hires) && metrics.includes(ApiWidgetMetric.terminations)) {
    return { systemKey: "headcount.over_time.joiners_leavers", splitByColumnId };
  }
  if (dimension && TERMINATION_DIMENSIONS.includes(dimension)) {
    return { systemKey: `headcount.by.${dimension}`, splitByColumnId: null };
  }
  const isStaffMetric =
    metrics[0] === ApiWidgetMetric.headcount ||
    metrics[0] === ApiWidgetMetric.headcount_growth_rate;
  if (dimension && isStaffMetric) {
    return { systemKey: `headcount.by.${dimension}.${metricKey}`, splitByColumnId: null };
  }
  return { systemKey: `headcount.over_time.${metricKey}`, splitByColumnId };
}

// Системный дашборд headcount (base/people_analytics/dashboards/people_analytics_dashboard_constants.py)
const HEADCOUNT_DASHBOARD_KEY = "headcount";

export function useHeadcountWidget(input: ApiWidgetPreviewInput, enabled: boolean) {
  const { t } = useTranslation();
  const dashboards = usePeopleAnalyticsDashboards();

  const { systemKey, splitByColumnId } = resolveSystemWidget(input);
  const widgetId =
    dashboards.data
      ?.find((dashboard) => dashboard.system_key === HEADCOUNT_DASHBOARD_KEY)
      ?.widgets.find((item) => item.widget.system_key === systemKey)?.widget.id ?? null;

  const dataInput = useMemo<ApiPeopleAnalyticsWidgetDataInput>(() => {
    const filter: ApiPeopleAnalyticsEmployeeFilter = {};
    (input.query.filters ?? []).forEach((item) => {
      (filter as Record<string, number[]>)[item.field] = item.ids;
    });
    return {
      date_from: input.period.date_from as Date,
      date_to: input.period.date_to as Date,
      date_grain: input.query.date_grain ?? null,
      filter,
      split_by_column_id: splitByColumnId,
    };
  }, [input, splitByColumnId]);
  const analytics = usePeopleAnalyticsWidgetData(widgetId, dataInput, enabled);

  const data = useMemo(
    () =>
      analytics.data && {
        ...analytics.data,
        series: analytics.data.series.map((series) => ({
          ...series,
          points: series.points.map((point) =>
            point.label ? point : { ...point, label: t("reporting:people_analytics_group_empty") }
          ),
        })),
      },
    [analytics.data, t]
  );

  return { data, isLoading: dashboards.isLoading || analytics.isLoading };
}
